import React from "react";
import PropTypes from "prop-types";

import {
  Grid,
  Card,
  Box,
  Typography,
  Stack,
} from "@mui/material";
import {
  AutoAwesomeOutlined as MRLIcon,
  CasinoOutlined as RevolverIcon,
  SportsEsportsOutlined as MineIcon,
  RocketLaunchOutlined as MissileIcon,
  ArrowForward as ArrowForwardIcon,
} from "@mui/icons-material";
import { colors, borderRadiusPx, customShadows } from "../../../../../theme";

const ENGINES = [
  {
    typeId: "2",
    title: "MegaBET",
    subtitle: "Raffle & Rollover Jackpots",
    icon: MRLIcon,
    accentColor: colors.emerald[600],
    bgColor: colors.emerald[50],
    borderColor: colors.emerald[200],
    gradient: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
    tag: "High Stakes",
  },
  {
    typeId: "0",
    title: "SpinBET",
    subtitle: "Roulette Wheel Lottery",
    icon: RevolverIcon,
    accentColor: colors.primary.main,
    bgColor: colors.blue[50],
    borderColor: colors.blue[200],
    gradient: "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
    tag: "Instant Spin",
  },
  {
    typeId: "1",
    title: "MineBET",
    subtitle: "Minesweeper Multiplier",
    icon: MineIcon,
    accentColor: colors.purple[600],
    bgColor: colors.purple[50],
    borderColor: colors.purple[200],
    gradient: "linear-gradient(135deg, #a855f7 0%, #7c3aed 100%)",
    tag: "Multipliers",
  },
  {
    typeId: "3",
    title: "RocketBET",
    subtitle: "Speed Blast Lottery",
    icon: MissileIcon,
    accentColor: colors.amber[600],
    bgColor: colors.amber[50],
    borderColor: colors.amber[200],
    gradient: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
    tag: "Fast Draw",
  },
];

const GameTypeShowcase = ({ countsByType, activeTab, onSelectTab }) => {
  return (
    <Box sx={{ mb: 4 }}>
      <Typography
        variant="subtitle2"
        sx={{
          fontWeight: 800,
          color: colors.slate[500],
          textTransform: "uppercase",
          letterSpacing: "0.8px",
          fontSize: "0.75rem",
          mb: 1.75,
        }}
      >
        Featured Game Engines
      </Typography>

      <Grid container spacing={2}>
        {ENGINES.map((engine) => {
          const count = countsByType[engine.typeId] || 0;
          const isSelected = String(activeTab) === String(engine.typeId);
          const EngineIcon = engine.icon;

          return (
            <Grid item xs={12} sm={6} md={3} key={engine.typeId}>
              <Card
                onClick={() => onSelectTab(engine.typeId)}
                sx={{
                  p: 2.25,
                  borderRadius: borderRadiusPx.xl,
                  backgroundColor: colors.dashboard.cardBg,
                  border: `1.5px solid ${isSelected ? engine.accentColor : colors.dashboard.cardBorder}`,
                  boxShadow: isSelected ? "0 8px 24px rgba(0, 0, 0, 0.08)" : customShadows.cardFlat,
                  cursor: "pointer",
                  position: "relative",
                  overflow: "hidden",
                  transition: "all 0.25s ease-in-out",
                  "&:hover": {
                    transform: "translateY(-3px)",
                    borderColor: engine.accentColor,
                    boxShadow: "0 10px 28px rgba(0, 0, 0, 0.08)",
                  },
                }}
              >
                {/* Top Mini Color Bar */}
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 3.5,
                    background: engine.gradient,
                  }}
                />

                <Stack spacing={1.5}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: borderRadiusPx.md,
                        bgcolor: engine.bgColor,
                        color: engine.accentColor,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <EngineIcon sx={{ fontSize: 20 }} />
                    </Box>

                    <Box
                      sx={{
                        px: 1,
                        py: 0.25,
                        borderRadius: borderRadiusPx.full,
                        bgcolor: engine.bgColor,
                        border: `1px solid ${engine.borderColor}`,
                        color: engine.accentColor,
                        fontSize: "0.7rem",
                        fontWeight: 700,
                      }}
                    >
                      {count} {count === 1 ? "Pool" : "Pools"}
                    </Box>
                  </Stack>

                  <Box>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 800,
                        color: colors.slate[900],
                        fontSize: "1.05rem",
                        lineHeight: 1.2,
                        mb: 0.25,
                      }}
                    >
                      {engine.title}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: colors.slate[500], fontSize: "0.75rem", display: "block" }}
                    >
                      {engine.subtitle}
                    </Typography>
                  </Box>

                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={0.5}
                    sx={{
                      color: isSelected ? engine.accentColor : colors.slate[600],
                      fontWeight: 700,
                      fontSize: "0.78rem",
                      pt: 0.5,
                    }}
                  >
                    <span>{isSelected ? "Active Filter" : "Explore Pools"}</span>
                    <ArrowForwardIcon sx={{ fontSize: 13 }} />
                  </Stack>
                </Stack>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

GameTypeShowcase.propTypes = {
  countsByType: PropTypes.object.isRequired,
  activeTab: PropTypes.string.isRequired,
  onSelectTab: PropTypes.func.isRequired,
};

export default GameTypeShowcase;
